import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { XCircle, CheckCircle, Warning, ArrowClockwise } from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
}

export default function CancelEngagement() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ phase: "loading" }); // loading | ready | already | error | confirming | confirmed
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!token) { setState({ phase: "error", message: "This cancellation link is missing its token." }); return; }
    api.get(`/cancel/preview?token=${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setPreview(data);
        if (data.cancellation_requested_at) setState({ phase: "already" });
        else setState({ phase: "ready" });
      })
      .catch((e) => setState({ phase: "error", message: formatApiErrorDetail(e.response?.data?.detail) || "We couldn't load your cancellation page." }));
  }, [token]);

  const confirm = async () => {
    setState({ phase: "confirming" });
    try {
      const { data } = await api.post(`/cancel/confirm?token=${encodeURIComponent(token)}`);
      setPreview((p) => ({ ...(p || {}), service_ends_at: data.service_ends_at || p?.service_ends_at }));
      setState({ phase: "confirmed", already: !!data.already_cancelled });
    } catch (e) {
      setState({ phase: "error", message: formatApiErrorDetail(e.response?.data?.detail) || "Cancellation failed." });
    }
  };

  return (
    <div className="min-h-screen paper-grain flex items-center justify-center px-6 py-12">
      <div className="max-w-2xl w-full rounded-2xl bg-white border border-cvc-border p-10 shadow-sm" data-testid="cancel-card">

        {state.phase === "loading" && (
          <div className="text-center">
            <ArrowClockwise size={44} weight="duotone" className="mx-auto text-cvc-navy animate-spin" />
            <p className="font-body text-cvc-ink-soft mt-4">Loading your cancellation page…</p>
          </div>
        )}

        {state.phase === "error" && (
          <div className="text-center">
            <Warning size={48} weight="duotone" className="mx-auto text-cvc-red" />
            <h1 className="font-heading text-3xl text-cvc-navy mt-4">We couldn't load this link.</h1>
            <p className="font-body text-cvc-ink-soft mt-3">{state.message}</p>
            <p className="font-body text-sm text-cvc-ink-soft mt-3">Please call us at <strong>435-535-7000</strong> and we'll take care of it personally.</p>
            <Link to="/" className="inline-block mt-8 font-body text-sm text-cvc-red underline underline-offset-4">← Back to Cache Valley Consulting</Link>
          </div>
        )}

        {(state.phase === "ready" || state.phase === "confirming") && preview && (
          <>
            <p className="text-xs tracking-[0.3em] uppercase font-body font-semibold text-cvc-red">Cancel engagement</p>
            <h1 data-testid="cancel-headline" className="font-heading text-4xl text-cvc-navy mt-3 leading-tight">
              Cancel services for {preview.company_name}?
            </h1>
            <p className="font-body text-cvc-ink-soft text-base mt-4 leading-relaxed">
              Before you confirm, here's exactly what happens — per the engagement letter you signed:
            </p>

            <div className="mt-6 rounded-xl bg-cvc-parchment/60 border border-cvc-border p-5 space-y-3">
              <p className="font-body text-sm text-cvc-ink leading-relaxed">
                <strong className="text-cvc-navy">Cancel anytime.</strong> No additional monthly charges will be made after we receive your cancellation.
              </p>
              <p className="font-body text-sm text-cvc-ink leading-relaxed">
                <strong className="text-cvc-navy">Your retainer covers the last month.</strong> The last-month retainer you paid up front
                {preview.monthly_total ? <> (<strong>${preview.monthly_total.toLocaleString()}</strong>) </> : " "}
                covers <em>one final calendar month of services</em> after your cancellation. After that, the engagement closes.
              </p>
              {preview.service_ends_at && (
                <p className="font-body text-sm text-cvc-ink leading-relaxed">
                  <strong className="text-cvc-navy">Your last day of services:</strong>{" "}
                  <span className="inline-block px-2.5 py-0.5 rounded-full bg-scaled-green/15 border border-scaled-green/30 text-scaled-green-deep font-semibold">{fmtDate(preview.service_ends_at)}</span>
                </p>
              )}
              <p className="font-body text-sm text-cvc-ink-soft leading-relaxed">
                <strong className="text-cvc-red">No refunds.</strong> Cache Valley Consulting is a professional service. Retainers and prior monthly fees are not refundable. Cancelling stops future monthly billing; it does not reverse past payments.
              </p>
              <p className="font-body text-xs text-cvc-ink-soft italic leading-relaxed">
                You signed these terms electronically when you joined. The relevant clauses are repeated in your engagement letter PDF.
              </p>
            </div>

            <p className="font-body text-sm text-cvc-ink-soft mt-6 leading-relaxed">
              We're sorry to see you go. If this is the right call for now, click below and we'll handle the rest immediately —
              your subscription stops at the end of its current billing cycle, and Jonathan will reach out about closing things out cleanly.
            </p>

            <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
              <Link to="/" className="font-body text-sm text-cvc-ink-soft underline underline-offset-4 hover:text-cvc-navy" data-testid="cancel-keep-services">
                ← Never mind, keep my services
              </Link>
              <Button
                data-testid="cancel-confirm-btn"
                onClick={confirm}
                disabled={state.phase === "confirming"}
                className="bg-cvc-red hover:bg-cvc-red-deep text-white rounded-full px-8 py-6 h-auto text-base font-body"
              >
                {state.phase === "confirming" ? "Cancelling…" : "Confirm cancellation"}
              </Button>
            </div>
          </>
        )}

        {state.phase === "already" && (
          <>
            <CheckCircle size={48} weight="duotone" className="mx-auto text-scaled-green" />
            <h1 className="font-heading text-3xl text-cvc-navy mt-4 text-center">Already cancelled.</h1>
            <p className="font-body text-cvc-ink-soft mt-3 text-center">
              Your engagement is already in its wind-down period.
              {preview?.service_ends_at && <> Your last day of services is <strong>{fmtDate(preview.service_ends_at)}</strong>.</>}
            </p>
            <p className="font-body text-sm text-cvc-ink-soft mt-3 text-center">
              Questions? Call <strong>435-535-7000</strong>.
            </p>
            <div className="text-center"><Link to="/" className="inline-block mt-8 font-body text-sm text-cvc-red underline underline-offset-4">← Back to Cache Valley Consulting</Link></div>
          </>
        )}

        {state.phase === "confirmed" && (
          <>
            <CheckCircle size={48} weight="duotone" className="mx-auto text-scaled-green" data-testid="cancel-confirmed-icon" />
            <h1 className="font-heading text-3xl text-cvc-navy mt-4 text-center">Cancellation received.</h1>
            <p className="font-body text-cvc-ink mt-4 text-center leading-relaxed">
              Your monthly billing has been stopped.
              {preview?.service_ends_at && <> Your <strong>last day of services</strong> is <strong>{fmtDate(preview.service_ends_at)}</strong>, covered by the last-month retainer you paid at signup.</>}
            </p>
            <p className="font-body text-cvc-ink-soft mt-3 text-center leading-relaxed">
              Jonathan has been notified and will be in touch to wrap things up cleanly. Thank you for the chance to walk part of this journey with you.
            </p>
            <div className="text-center"><Link to="/" className="inline-block mt-8 font-body text-sm text-cvc-red underline underline-offset-4">← Back to Cache Valley Consulting</Link></div>
          </>
        )}
      </div>
    </div>
  );
}
